import type { HelpCentre } from "../types";

export type HelpSearchHit = {
  slug: string;
  title: string;
  summary: string;
  category: string;
};

/** Lower case, no accents and `ł` as `l`, so "zaplac" finds "Zapłać". */
const fold = (text: string) =>
  text.toLowerCase().replace(/ł/g, "l").normalize("NFKD").replace(/[̀-ͯ]/g, "");

/**
 * The articles that hold every word typed, in the title or the summary. A hit whose title holds
 * them all ranks above one that needed the summary, and an earlier hit in the title above a later
 * one; otherwise the help centre's own order stands.
 */
export const searchHelp = (centre: HelpCentre, query: string, limit = 8): HelpSearchHit[] => {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  return centre.categories
    .flatMap((category) =>
      category.articles.map((article) => ({
        hit: { slug: article.slug, title: article.title, summary: article.summary, category: category.name },
        title: fold(article.title),
        text: fold(`${article.title} ${article.summary}`),
      })),
    )
    .filter((entry) => words.every((word) => entry.text.includes(word)))
    .map((entry, order) => {
      const inTitle = words.every((word) => entry.title.includes(word));
      return { hit: entry.hit, order, rank: inTitle ? entry.title.indexOf(words[0] ?? "") : 1000 };
    })
    .toSorted((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, limit)
    .map((entry) => entry.hit);
};
